import { FC, ReactNode, useContext } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Kennzeichen, { KennzeichenArt } from './Kennzeichen';
import { UserContext } from '../../contexts/UserContext';

/**
 * Ein Abschnitt einer Marketing-Seite (Karte mit Titel, Kennzeichen, einem Satz Erklärung).
 * `eingeklappt` macht daraus ein zugeklapptes Akkordeon — für alles, was Marketing nicht ausfüllen muss.
 */
export const Abschnitt: FC<{
  titel: string;
  art?: KennzeichenArt;
  erklaerung?: ReactNode;
  /** Zugeklappt starten (Optionales, Automatisches). */
  eingeklappt?: boolean;
  /** Rechts im Kopf (Status-Chip, Knopf). */
  kopfRechts?: ReactNode;
  children: ReactNode;
  id?: string;
}> = ({ titel, art, erklaerung, eingeklappt, kopfRechts, children, id }) => {
  const kopf = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
      <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
        {titel}
      </Typography>
      {art && <Kennzeichen art={art} />}
      <Box sx={{ flex: 1 }} />
      {kopfRechts}
    </Box>
  );
  if (eingeklappt) {
    return (
      <Accordion
        id={id}
        disableGutters
        elevation={0}
        sx={{
          mb: 2,
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          '&:before': { display: 'none' },
          bgcolor: '#ffffff',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ width: '100%', pr: 1 }}>
            {kopf}
            {erklaerung && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 0.25 }}
              >
                {erklaerung}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    );
  }
  return (
    <Paper
      id={id}
      variant="outlined"
      sx={{ p: 2, mb: 2, bgcolor: '#ffffff', borderColor: '#e5e7eb' }}
    >
      {kopf}
      {erklaerung && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 1.5, maxWidth: '90ch' }}
        >
          {erklaerung}
        </Typography>
      )}
      {!erklaerung && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Paper>
  );
};

/** Zugeklappter Bereich „Weitere Einstellungen (optional)“ innerhalb eines Abschnitts. */
export const MehrOptionen: FC<{ titel?: string; erklaerung?: ReactNode; children: ReactNode }> = ({
  titel = 'Weitere Einstellungen',
  erklaerung = 'Optional — kann leer bleiben, dann gilt der jeweils genannte Standard.',
  children,
}) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      mt: 1,
      border: '1px dashed #d1d5db',
      borderRadius: '6px',
      '&:before': { display: 'none' },
      bgcolor: '#fcfcfd',
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">{titel}</Typography>
          <Kennzeichen art="optional" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {erklaerung}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

/** `true`, wenn der angemeldete Nutzer Admin ist. Ohne Kontext (Tests): false. */
export function useIstAdmin(): boolean {
  return !!useContext(UserContext)?.is_admin;
}

/**
 * Nur für den Admin sichtbar. `bereich` = zugeklappter Kasten „Technik (nur Admin)“ mit Erklärung;
 * ohne `bereich` werden die Kinder einfach nur für den Admin gerendert.
 * Achtung: das ist eine ANZEIGE-Weiche. Wo ein Schreibweg für Marketing wirklich zu sein muss, gehört der
 * Riegel zusätzlich ins Backend.
 */
export const NurAdmin: FC<{
  bereich?: boolean;
  titel?: string;
  erklaerung?: ReactNode;
  children: ReactNode;
}> = ({
  bereich,
  titel = 'Technik',
  erklaerung = 'Technische Einstellungen. Für Marketing nicht nötig — nur sichtbar, weil du Admin bist.',
  children,
}) => {
  const admin = useIstAdmin();
  if (!admin) return null;
  if (!bereich) return <>{children}</>;
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid #d9d0fb',
        borderRadius: '6px',
        '&:before': { display: 'none' },
        bgcolor: '#faf8ff',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">{titel}</Typography>
            <Kennzeichen art="admin" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {erklaerung}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};
